/* assets/prepost-selector.js
 * ---------------------------------------------------------------------------
 * Controller for the three-tab kit selector (PRE / POST / PRE + POST).
 *
 * CART CONTRACT
 *   The kit PARENT product is never sold. Only the selected CHILD products go
 *   in, each at its own price and quantity, tagged with a Kit line property.
 *   That Kit property is what the cart snippet groups by, so it must be set.
 *
 * HOW THE PARENT IS KEPT OUT, WITHOUT LOSING THE DRAWER
 *   This theme opens AND PAINTS the cart drawer only as a side-effect of its
 *   own add-to-cart flow running to completion. So we keep that flow: after
 *   our own /cart/add.js we replay the click on the theme's button and let its
 *   native handler run.
 *
 *   Normally that replay would also submit the parent variant. On a kit
 *   template it cannot, because product-buy-buttons.liquid renders no
 *   <input name="id"> there — the theme's request carries no variant, so
 *   Shopify creates no line. The flow runs, the drawer opens and paints, and
 *   nothing gets added.
 *
 *   An earlier attempt removed <product-buy-buttons-element> altogether. That
 *   stopped the parent, but it also killed the drawer, because there was no
 *   theme handler left to run. Do not remove the component.
 *
 * WHY ONE PILL CAN PRODUCE TWO CART LINES
 *   On the bundle, a product carrying pre-op units AND recovery units becomes
 *   two lines: pre-op one-time, recovery on the selling plan. A merged line
 *   would refill the wrong amount every cycle.
 *
 * ONE CARD, MANY PLAN IDS
 *   A selling plan id belongs to ONE product. Whey's plan id on a Methyl B-12
 *   line makes /cart/add.js reject the WHOLE request. resolvePlanFor() runs
 *   per pill at cart time: exact id, then plan name, then the product's only
 *   plan.
 *
 * THE EMPTY DRAWER
 *   1. VISIBILITY. .cart--item carries data-transition-item and only gets
 *      data-transition-active="true" when Liquid renders with `async`. A
 *      /?sections= fetch is not async, so injected items arrive without it
 *      and sit at opacity 0. We set it after every paint.
 *   2. THE COMPONENT'S OWN STATE. cart-element is a custom element; swapping
 *      its innerHTML leaves the SAME instance, so it keeps whatever
 *      empty/not-empty state it decided on at construction — the empty
 *      message sitting on top of real lines. We replace the NODE, which
 *      forces a fresh construction, and mirror the theme's empty toggle.
 *   3. CONVERGENCE. We re-check until the drawer's line count matches
 *      /cart.js, with an empty section render never allowed to overwrite a
 *      drawer that is already showing lines.
 *
 * SELECTION MODES (emitted by Liquid as data-select)
 *   locked / group-single / scope-single / scope-toggle / toggle
 * ------------------------------------------------------------------------- */

(function () {
  'use strict';

  var LOG = '[prepost-selector]';

  // ?ppdebug=1 on any storefront URL shows the plan diagnostic in the page.
  var DEBUG = /[?&]ppdebug=1/.test(window.location.search);

  // Every drawer line-item pattern we know about, in one place.
  var ITEM_SEL = '.cart--item, .cart-item, [data-cart-item], [data-line-item], .cart__item';

  // Tabs that only ever sell the subscription, unless the panel says
  // otherwise with data-plan-only="false".
  var PLAN_ONLY_PHASES = ['bundle', 'prepost', 'pre-post', 'pre_post', 'pre+post'];

  // Product templates whose page product is an empty kit container. Keep in
  // sync with the is_kit_template check in product-buy-buttons.liquid.
  var KIT_TEMPLATE_SUFFIXES = ['pre-post-kits', 'bundle-kits', 'new-kits'];

  var THEME_BUY_SELECTOR = [
    '.product-buy-buttons--primary',
    'button[name="add"]',
    'input[name="add"]',
    '[type="submit"][name="add"]',
    '.product-form--submit',
    '.product-form__submit',
    '[data-add-to-cart]'
  ].join(',');

  /* ---------- small helpers -------------------------------------------- */

  function qsa(root, sel) {
    return [].slice.call(root.querySelectorAll(sel));
  }

  function intAttr(el, key) {
    var n = parseInt(el.dataset[key], 10);
    return isNaN(n) ? 0 : n;
  }

  function floatAttr(el, key) {
    var n = parseFloat(el.dataset[key]);
    return isNaN(n) ? 0 : n;
  }

  function pillPlans(pill) {
    var node = pill.querySelector('[data-pp-plans]');
    if (!node) return [];
    try {
      var parsed = JSON.parse(node.textContent || '[]');
      return parsed && parsed.length ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function wait(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  // Are we on a template whose page product must never be added? Read from
  // the body class Shopify emits for the template suffix.
  function isKitTemplate() {
    var cls = (document.body && document.body.className) || '';
    return KIT_TEMPLATE_SUFFIXES.some(function (s) {
      return cls.indexOf(s) !== -1;
    });
  }

  // The variant the PAGE is for — the kit parent. Never submitted on a kit
  // template (no name="id" field exists there), but the selector still needs
  // to recognise it so it can refuse to add it as one of its own children.
  function pageVariantId() {
    var holder = document.querySelector('product-buy-buttons-element[data-variant-id]');
    if (holder && holder.dataset.variantId) return String(holder.dataset.variantId);

    var input = document.querySelector('.product-buy-buttons--input');
    if (input && input.value) return String(input.value);

    return null;
  }

  /* ---------- hiding the parent line ------------------------------------ */
  /* Only used on non-kit templates, where the theme's replay really does add
   * the parent and we have to delete it afterwards. */

  function hideVariantInDrawer(variantId) {
    if (!variantId) return;
    var style = document.getElementById('pp-hide-main-style');
    if (!style) {
      style = document.createElement('style');
      style.id = 'pp-hide-main-style';
      document.head.appendChild(style);
    }
    style.textContent =
      '.cart--item[data-variant-id="' + variantId + '"],' +
      '.cart-item[data-variant-id="' + variantId + '"],' +
      '[data-cart-item][data-variant-id="' + variantId + '"]' +
      '{display:none !important;}';
  }

  function unhideVariantInDrawer() {
    var style = document.getElementById('pp-hide-main-style');
    if (style) style.textContent = '';
  }

  /* ---------- making a repainted drawer actually visible ----------------- */

  // Items rendered by /?sections= come back WITHOUT data-transition-active,
  // because Liquid only sets it when `async` is truthy. The theme's
  // transition controller never sees nodes we injected, so without this they
  // stay at opacity 0 and the drawer looks empty even when it is painted.
  function activateTransitions(scope) {
    if (!scope) return;
    qsa(scope, '[data-transition-item], [data-transition-container]')
      .forEach(function (el) {
        el.setAttribute('data-transition-active', 'true');
      });
  }

  // .cart--empty is always in the DOM; something else decides whether it
  // shows. The theme's own cart script does exactly this, so we mirror it
  // rather than guessing at a CSS rule.
  function applyEmptyState(cartEl, itemCount) {
    if (!cartEl || typeof itemCount !== 'number') return;
    var isEmpty = itemCount === 0;

    qsa(cartEl, '.cart--body, .cart--footer, .cart--notes, .cart--summary, .cart--headings')
      .forEach(function (el) { el.style.display = isEmpty ? 'none' : ''; });

    var empty = cartEl.querySelector('.cart--empty');
    if (empty) empty.style.display = isEmpty ? '' : 'none';

    cartEl.classList.toggle('is-empty', isEmpty);
  }

  // Pull the cart-element matching THIS view out of a section render. The
  // section can contain both the drawer and the desktop cart; a bare
  // querySelector('cart-element') can hand back the wrong one.
  function pickCartElement(container, view) {
    return (view && container.querySelector('cart-element[data-view="' + view + '"]')) ||
           container.querySelector('cart-element') ||
           container.querySelector('cart-drawer') ||
           container.firstElementChild;
  }

  // Replace the cart-element NODE, not its innerHTML.
  //
  // cart-element is a custom element. Swapping innerHTML leaves the SAME
  // instance in place, so its connectedCallback never re-runs and it keeps
  // whatever empty/not-empty state it decided on at construction. That is why
  // the drawer showed "Your cart is currently empty" directly above five
  // fully-rendered .cart--item nodes, and why a reload fixed it.
  function adoptCartElement(oldEl, freshNode) {
    if (!oldEl || !freshNode || !oldEl.parentNode) return null;

    var imported = document.importNode(freshNode, true);

    // Keep attributes the live page owns and the render can't know about.
    ['data-section-id', 'data-view'].forEach(function (attr) {
      var v = oldEl.getAttribute(attr);
      if (v && !imported.getAttribute(attr)) imported.setAttribute(attr, v);
    });

    oldEl.parentNode.replaceChild(imported, oldEl);
    return imported;
  }

  // Fetch a section render and report how many lines it holds, WITHOUT
  // painting it. Separating fetch from paint is what lets us refuse to
  // overwrite a good drawer with an empty render.
  function renderSection(url, sectionId, view) {
    return fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var html = data[sectionId];
        if (!html) return null;
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = pickCartElement(tmp, view);
        if (!fresh) return null;
        return { node: fresh, count: fresh.querySelectorAll(ITEM_SEL).length };
      })
      .catch(function () { return null; });
  }

  /* ---------- is the drawer actually open? ------------------------------ */

  var DRAWER_SELECTOR = [
    'cart-element[data-view="drawer"]',
    'cart-drawer', 'cart-drawer-element', 'cart-notification',
    '[data-cart-drawer]', '#CartDrawer', '#cart-drawer',
    '.cart-drawer', '.drawer--cart', '.js-cart-drawer'
  ].join(',');

  function drawerNodes() {
    return qsa(document, DRAWER_SELECTOR);
  }

  // On screen, not just present. Off-canvas drawers keep their box, they just
  // sit past the viewport edge, so the rect position is what tells us.
  function isOnScreen(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var cs = getComputedStyle(el);
    if (cs.display === 'none' || cs.visibility === 'hidden') return false;
    if (parseFloat(cs.opacity) === 0) return false;

    var r = el.getBoundingClientRect();
    if (r.width < 40 || r.height < 40) return false;
    if (r.right <= 8 || r.left >= window.innerWidth - 8) return false;
    if (r.bottom <= 8 || r.top >= window.innerHeight - 8) return false;
    return true;
  }

  function drawerIsOpen() {
    return drawerNodes().some(isOnScreen);
  }

  var DRAWER_EVENTS = [
    'cart:open', 'cart:refresh', 'cart:build', 'cart:added', 'cart:updated',
    'cart:toggle', 'cart-drawer:open', 'drawer:open', 'on:cart-drawer:open',
    'theme:cart:open', 'theme:cart:added', 'theme:drawer:open', 'openCartDrawer'
  ];

  var OPENER_SELECTORS = [
    '[data-drawer-toggle="cart-drawer"]',
    '[data-drawer-toggle="cart"]',
    '[data-cart-toggle]',
    '[data-drawer-trigger="cart"]',
    '[data-cart-drawer-toggle]',
    '[data-action="open-cart"]',
    '[aria-controls="cart-drawer"]',
    '[aria-controls*="cart" i]',
    '.header--cart',
    '.header--cart-link',
    '[data-cart-count]'
  ];

  // Ordered cheapest and safest first. Each entry may carry a revert() used
  // when the attempt did not open anything, so a failed guess leaves no
  // stray classes behind.
  function buildDrawerAttempts(root) {
    var list = [];
    var nodes = drawerNodes();

    // This theme's own drawer component: drawer-element.open('cart-drawer').
    var drawerEl = document.querySelector('drawer-element');
    if (drawerEl) {
      ['open', 'openDrawer', 'show'].forEach(function (method) {
        if (typeof drawerEl[method] !== 'function') return;
        list.push({
          name: 'drawer-element.' + method + '("cart-drawer")',
          run: function () { drawerEl[method]('cart-drawer'); }
        });
      });
    }

    ['open', 'show', 'openDrawer', 'setActive'].forEach(function (method) {
      nodes.forEach(function (node) {
        if (typeof node[method] !== 'function') return;
        list.push({
          name: node.tagName.toLowerCase() + '.' + method + '()',
          run: function () { node[method](true); }
        });
      });
    });

    OPENER_SELECTORS.forEach(function (sel) {
      list.push({
        name: 'click ' + sel,
        run: function () {
          var el = document.querySelector(sel);
          if (el) el.click();
        }
      });
    });

    list.push({
      name: 'dispatch theme events',
      run: function () {
        DRAWER_EVENTS.forEach(function (name) {
          document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { open: true, drawer: 'cart-drawer' } }));
          document.documentElement.dispatchEvent(
            new CustomEvent(name, { bubbles: true, detail: { open: true } })
          );
          nodes.forEach(function (node) {
            node.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { open: true } }));
          });
        });
      }
    });

    // The theme's own side-drawer markup, opened by attribute.
    list.push({
      name: 'drawer--side[data-side="right"] aria-expanded',
      run: function () {
        var side = document.querySelector('drawer-element .drawer--side[data-side="right"]');
        if (!side) return;
        side.setAttribute('aria-expanded', 'true');
        var overlay = document.querySelector('drawer-element .drawer--overlay');
        if (overlay) overlay.removeAttribute('aria-hidden');
        document.body.classList.add('drawer-open', 'overflow-hidden', 'js-drawer-open');
      },
      revert: function () {
        var side = document.querySelector('drawer-element .drawer--side[data-side="right"]');
        if (side) side.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('drawer-open', 'overflow-hidden', 'js-drawer-open');
      }
    });

    // Brute force. Reverted if it doesn't work, because a stuck open class
    // is worse than a closed drawer.
    var CLASSES = ['is-open', 'open', 'active', 'is-active', 'visible', 'drawer--open'];
    var BODY_CLASSES = ['cart-drawer-open', 'drawer-open', 'cart-open', 'js-drawer-open', 'has-drawer-open'];
    list.push({
      name: 'force open state',
      run: function () {
        nodes.forEach(function (node) {
          node.removeAttribute('hidden');
          node.setAttribute('open', '');
          node.setAttribute('aria-hidden', 'false');
          node.dataset.open = 'true';
          CLASSES.forEach(function (c) { node.classList.add(c); });
        });
        BODY_CLASSES.forEach(function (c) {
          document.body.classList.add(c);
          document.documentElement.classList.add(c);
        });
      },
      revert: function () {
        nodes.forEach(function (node) {
          node.removeAttribute('open');
          delete node.dataset.open;
          CLASSES.forEach(function (c) { node.classList.remove(c); });
        });
        BODY_CLASSES.forEach(function (c) {
          document.body.classList.remove(c);
          document.documentElement.classList.remove(c);
        });
      }
    });

    // Opt in with data-drawer-open-link="true". Off by default because on a
    // theme without a drawer handler this navigates to /cart.
    if (root && root.dataset.drawerOpenLink === 'true') {
      list.push({
        name: 'click cart link',
        run: function () {
          var el = document.querySelector('a[href$="/cart"]');
          if (el) el.click();
        }
      });
    }

    return list;
  }

  // Runs the attempts in order, stops at the first one that puts the drawer
  // on screen, and resolves with its name (or null).
  function tryToOpenDrawer(root) {
    if (drawerIsOpen()) return Promise.resolve('already open');

    var list = buildDrawerAttempts(root);
    var winner = null;
    var chain = Promise.resolve();

    list.forEach(function (attempt) {
      chain = chain.then(function () {
        if (winner) return;
        try { attempt.run(); } catch (e) {}
        return wait(90).then(function () {
          if (drawerIsOpen()) { winner = attempt.name; return; }
          if (attempt.revert) { try { attempt.revert(); } catch (e) {} }
        });
      });
    });

    return chain.then(function () { return winner; });
  }

  function isCartAddUrl(url) {
    try {
      return new URL(url, window.location.origin).pathname.indexOf('/cart/add') === 0;
    } catch (e) {
      return false;
    }
  }

  // Watches for the theme's own /cart/add so the parent line can be removed
  // the moment it lands. Only ever armed on a non-kit template.
  (function patchFetchForReplay() {
    if (window.__ppCartPatchInit) return;
    window.__ppCartPatchInit = true;

    var originalFetch = window.fetch;

    window.fetch = function (input, init) {
      var url = (typeof input === 'string') ? input : (input && input.url);
      var method = ((init && init.method) ||
                    (typeof input === 'object' && input && input.method) ||
                    'GET').toUpperCase();

      var result = originalFetch.apply(this, arguments);

      if (url && method === 'POST' && isCartAddUrl(url) && window.__ppPendingMainRemoval) {
        result.then(function () {
          var id = window.__ppPendingMainRemoval;
          if (!id) return;
          window.__ppPendingMainRemoval = null;
          // Let the theme finish painting the drawer before we mutate it.
          setTimeout(function () {
            if (typeof window.__ppRemoveMain === 'function') window.__ppRemoveMain(id);
          }, 120);
        }).catch(function () {});
      }

      return result;
    };
  })();

  /* ---------- money ----------------------------------------------------- */

  // Liquid's `escape` double-escapes money formats stored with HTML entities
  // (&pound;, &#36;), so '&amp;pound;' arrives here and would render as text.
  function decodeEntities(str) {
    if (!str || str.indexOf('&') === -1) return str;
    var ta = document.createElement('textarea');
    ta.innerHTML = str;
    return ta.value;
  }

  function formatWithDelimiters(cents, precision, thousands, decimal) {
    if (isNaN(cents) || cents === null) return '0';
    var value = (cents / 100.0).toFixed(precision);
    var parts = value.split('.');
    var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
    return whole + (parts[1] ? decimal + parts[1] : '');
  }

  // Honours the shop's own money_with_currency_format, so PKR, AED and GBP all
  // render correctly without hardcoding a symbol or separator.
  function formatMoney(cents, format) {
    var fmt = format || '{{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = fmt.match(placeholder);
    if (!match) return fmt;

    var value;
    switch (match[1]) {
      case 'amount':                                  value = formatWithDelimiters(cents, 2, ',', '.'); break;
      case 'amount_no_decimals':                      value = formatWithDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator':             value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator':        value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default:                                        value = formatWithDelimiters(cents, 2, ',', '.');
    }
    return fmt.replace(placeholder, value);
  }

  /* ---------- controller ------------------------------------------------ */

  function PrePostSelector(root) {
    this.root = root;
    this.moneyFormat = decodeEntities(root.dataset.moneyFormat) || '{{amount}}';
    this.afterAdd = root.dataset.afterAdd || 'drawer';
    this.busy = false;

    this.tabs = qsa(root, '[data-pp-tab]');
    this.panels = qsa(root, '[data-pp-panel]');
    this.addBtn = root.querySelector('[data-pp-add]');
    this.errorEl = root.querySelector('[data-role="pp-error"]');
    this.footerTotal = root.querySelector('[data-role="footer-total"]');
    this.headlinePrice = root.querySelector('[data-role="headline-price"]');
    this.headlineCompare = root.querySelector('[data-role="headline-compare"]');
    this.headlineSave = root.querySelector('[data-role="headline-save"]');

    this.themePriceTargets = this.findThemePriceTargets();

    // 'bind' (default) hijacks the theme's Add to cart. 'hide' is the old
    // behaviour, kept for themes where the button can't be safely reused.
    this.themeButtonMode = root.dataset.themeButtonMode || 'bind';
    if (this.themeButtonMode === 'hide') {
      this.hideThemeBuyButtons();
    } else {
      this.startThemeButtonWatch();
    }

    // How the drawer gets opened after an add:
    //   'theme'  (default) replay the theme's button so its native flow opens
    //            AND paints the drawer. On a kit template that flow carries no
    //            variant, so it adds nothing.
    //   'events' open and paint it entirely ourselves. Fallback only.
    this.drawerMode = root.dataset.drawerOpen || 'theme';

    // The fetch watcher lives outside the instance, so hand it a way back in.
    var self = this;
    window.__ppRemoveMain = function (id) { return self.removeMainAndRefresh(id); };

    if (!this.panels.length) {
      console.warn(LOG, 'no panels found — check the block case is intact.');
    }
    if (!root.querySelector('[data-pp-pill]')) {
      console.warn(LOG, 'no pills rendered — check the kit products have their ' +
        'custom.protein_products / core_supplements / vitamin_protocol metafields set.');
    }

    this.bind();
    // Paint the tabs and totals first so the block is usable immediately; the
    // plan fetch resolves a moment later.
    if (root.dataset.hideDynamicCheckout === 'true') this.hideDynamicCheckout();
    this.setPhase(root.dataset.activePhase || 'pre', { focus: false });
    this.hydratePlans();
    this.warnOnFallbackQuantities();
  }

  // The theme's price node lives outside this block, so without this the big
  // price at the top of the page would sit frozen on the parent's 0.00.
  PrePostSelector.prototype.findThemePriceTargets = function () {
    var custom = this.root.dataset.themePriceSelector;
    var selector = custom && custom.trim()
      ? custom.trim()
      : '.product-price--root .price-item--regular, .product-price--original, .price__regular .price-item--regular, [data-product-price], .product__price';

    var root = this.root;
    var nodes = qsa(document, selector).filter(function (n) { return !root.contains(n); });

    if (!nodes.length) {
      console.warn(LOG, 'no theme price element matched "' + selector +
        '". Set the "Theme price selector" block setting.');
    }
    return nodes;
  };

  // A handle that matches nothing in the customizer string falls back to 1
  // SILENTLY in Liquid. That is how whey-protein:3 shipped as a single tub.
  // A panel where EVERY pill resolved to exactly 1 is the signature of a
  // whole string missing or every handle being wrong.
  PrePostSelector.prototype.warnOnFallbackQuantities = function () {
    this.panels.forEach(function (panel) {
      var pills = qsa(panel, '[data-pp-pill]');
      if (pills.length < 2) return;

      var allOne = pills.every(function (p) {
        return (intAttr(p, 'qtyPre') + intAttr(p, 'qtyPost')) === 1;
      });

      if (allOne) {
        console.warn(LOG, 'panel "' + panel.dataset.phase + '": every pill ' +
          'resolved to quantity 1. If you set quantities in the customizer, ' +
          'the handles do not match. Run window.__ppQty() for the real ones.');
      }
    });
  };

  /* ---------- theme add to cart button ---------------------------------- */

  PrePostSelector.prototype.themeBuyButtons = function () {
    var root = this.root;
    var custom = root.dataset.themeFormSelector;
    var selector = custom && custom.trim() ? custom.trim() : THEME_BUY_SELECTOR;

    return qsa(document, selector).filter(function (el) {
      return el !== root && !el.contains(root) && !root.contains(el);
    });
  };

  // NEVER HIDE THE FORM ITSELF. main-product.liquid wraps every block in
  // <form action="/cart/add">, so these buttons are DESCENDANTS of that form.
  // Hiding the form takes the whole product column down, this block included.
  PrePostSelector.prototype.hideThemeBuyButtons = function () {
    this.themeBuyButtons().forEach(function (el) {
      var wrap = el.closest('.product-buy-buttons--root, .product-form--submit-wrapper, .product-form__buttons') || el;
      wrap.style.display = 'none';
    });
  };

  PrePostSelector.prototype.bindThemeBuyButtons = function () {
    var self = this;

    this.themeBuyButtons().forEach(function (btn) {
      if (btn.__ppBound) return;
      btn.__ppBound = true;

      // Capture phase, and stopImmediatePropagation, because the theme has
      // its own listener on this exact button. Let it run on the FIRST click
      // and it would submit the form instead of our selection.
      btn.addEventListener('click', function (e) {
        // ...except on the replay, where the native handler running IS the
        // point: it is what opens and paints the drawer.
        if (btn.dataset.ppReplay === 'true') {
          delete btn.dataset.ppReplay;
          return;
        }

        e.preventDefault();
        e.stopImmediatePropagation();
        self.lastThemeBtn = btn;
        self.addToCart();
      }, true);
    });
  };

  // The parent kit has no sellable stock of its own, so the theme keeps
  // flipping its button back to disabled / "Sold out" after every variant
  // render. Nothing else puts it back.
  PrePostSelector.prototype.enableThemeBuyButtons = function () {
    var label = this.root.dataset.addLabel || 'Add to cart';

    this.themeBuyButtons().forEach(function (btn) {
      if (btn.hasAttribute('disabled')) {
        btn.removeAttribute('disabled');
        btn.setAttribute('data-enabled', 'true');
      }
      var txt = btn.querySelector('.product-buy-buttons--cta-text');
      if (txt && /sold out|out of stock|unavailable/i.test(txt.textContent)) {
        txt.textContent = label;
      }
    });
  };

  // One interval covers both jobs: the theme re-renders the buy buttons on
  // every variant change, which drops our listener and re-disables them.
  PrePostSelector.prototype.startThemeButtonWatch = function () {
    var self = this;
    this.bindThemeBuyButtons();
    this.enableThemeBuyButtons();

    if (this.themeButtonTimer) clearInterval(this.themeButtonTimer);
    this.themeButtonTimer = setInterval(function () {
      self.bindThemeBuyButtons();
      self.enableThemeBuyButtons();
    }, 500);
  };

  PrePostSelector.prototype.bind = function () {
    var self = this;

    // Delegated, so nothing breaks if a panel is re-rendered by the editor or
    // if the plan card is injected after boot.
    this.root.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-pp-tab]');
      if (tab && self.root.contains(tab)) {
        e.preventDefault();
        return self.setPhase(tab.dataset.phaseTarget, { focus: false });
      }

      var pill = e.target.closest('[data-pp-pill]');
      if (pill && self.root.contains(pill)) {
        e.preventDefault();
        return self.togglePill(pill);
      }

      var opt = e.target.closest('[data-pp-option]');
      if (opt && self.root.contains(opt)) {
        e.preventDefault();
        return self.selectOption(opt);
      }

      var add = e.target.closest('[data-pp-add]');
      if (add && self.root.contains(add)) {
        e.preventDefault();
        return self.addToCart();
      }
    });

    this.root.addEventListener('keydown', function (e) {
      var hit = e.target.closest('[data-pp-tab],[data-pp-pill],[data-pp-option],[data-pp-add]');
      if (!hit || !self.root.contains(hit)) return;

      if (hit.hasAttribute('data-pp-tab') && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
        e.preventDefault();
        var i = self.tabs.indexOf(hit);
        var next = self.tabs[(i + (e.key === 'ArrowRight' ? 1 : self.tabs.length - 1)) % self.tabs.length];
        if (next) self.setPhase(next.dataset.phaseTarget, { focus: true });
        return;
      }

      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      // This block sits inside the theme's product form, so an unswallowed
      // Enter would submit it.
      e.preventDefault();
      hit.click();
    });

    this.root.addEventListener('change', function (e) {
      if (e.target.matches('[data-pp-surgery-date]')) self.clearError();
    });
  };

  PrePostSelector.prototype.activePanel = function () {
    return this.root.querySelector('[data-pp-panel]:not([hidden])');
  };

  PrePostSelector.prototype.setPhase = function (phase, opts) {
    opts = opts || {};
    var self = this;

    this.tabs.forEach(function (tab) {
      var on = tab.dataset.phaseTarget === phase;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
      // Only steal focus for keyboard navigation. Focusing on a mouse click
      // scroll-jumps the page on mobile.
      if (on && opts.focus === true) tab.focus();
    });

    this.panels.forEach(function (panel) {
      var on = panel.dataset.phase === phase;
      panel.hidden = !on;
      panel.classList.toggle('is-active-panel', on);
      if (!on) return;
      self.root.dataset.activePhase = phase;
    });

    this.enforcePlanOnly(this.activePanel());
    this.clearError();
    this.recalculate();
  };

  /* ---------- subscription-only tabs ------------------------------------ */

  PrePostSelector.prototype.isPlanOnly = function (panel) {
    if (!panel) return false;
    if (panel.dataset.planOnly === 'true') return true;
    if (panel.dataset.planOnly === 'false') return false;
    return PLAN_ONLY_PHASES.indexOf((panel.dataset.phase || '').toLowerCase()) !== -1;
  };

  // On a plan-only tab the one-time card is pulled and the plan card is
  // selected for the customer. The pull only happens once a plan card
  // actually exists — if ReCharge is unreachable, one-time stays as the
  // fallback so the tab can still be bought.
  PrePostSelector.prototype.enforcePlanOnly = function (panel) {
    if (!this.isPlanOnly(panel)) return;

    var self = this;

    qsa(panel, '[data-pp-options]').forEach(function (group) {
      var plan = group.querySelector('[data-pp-option][data-kind="plan"]');
      if (!plan) return;

      var others = qsa(group, '[data-pp-option]').filter(function (o) {
        return o.dataset.kind !== 'plan';
      });

      others.forEach(function (opt) {
        opt.hidden = true;
        opt.setAttribute('aria-hidden', 'true');
        opt.setAttribute('tabindex', '-1');
        opt.setAttribute('aria-checked', 'false');
        opt.classList.remove('is-selected');
      });

      // One visible option left. Hook for CSS that drops the group heading
      // or the radio dot on this tab.
      group.classList.toggle('pp-options--plan-only', others.length > 0);

      var current = group.querySelector('[data-pp-option][aria-checked="true"]:not([hidden])');
      if (!current) self.selectOption(plan);
    });
  };

  PrePostSelector.prototype.togglePill = function (pill) {
    if (pill.getAttribute('aria-disabled') === 'true') return;

    var mode = pill.dataset.select || 'toggle';
    if (mode === 'locked') return;

    var panel = pill.closest('[data-pp-panel]');
    if (!panel) return;

    var isOn = pill.getAttribute('aria-checked') === 'true';

    function off(el) {
      el.setAttribute('aria-checked', 'false');
      el.classList.remove('is-active');
    }
    function on(el) {
      el.setAttribute('aria-checked', 'true');
      el.classList.add('is-active');
    }

    if (mode === 'group-single') {
      qsa(panel, '[data-pp-pill][data-group="' + pill.dataset.group + '"]').forEach(off);
      on(pill);
    } else if (mode === 'scope-single') {
      qsa(panel, '[data-pp-pill][data-scope="' + pill.dataset.scope + '"]').forEach(off);
      on(pill);
    } else if (mode === 'scope-toggle') {
      qsa(panel, '[data-pp-pill][data-scope="' + pill.dataset.scope + '"]').forEach(off);
      if (!isOn) on(pill);
    } else {
      if (isOn) off(pill); else on(pill);
    }

    this.clearError();
    this.recalculate();
  };

  PrePostSelector.prototype.selectOption = function (option) {
    var group = option.closest('[data-pp-options]');
    if (!group) return;

    qsa(group, '[data-pp-option]').forEach(function (o) {
      o.setAttribute('aria-checked', 'false');
      o.classList.remove('is-selected');
    });
    option.setAttribute('aria-checked', 'true');
    option.classList.add('is-selected');

    // Only the plan card reveals the surgery date, so switching back to
    // one-time does not leave a stray field behind.
    var details = group.querySelector('[data-pp-details]');
    if (details) details.hidden = option.dataset.revealDetails !== 'true';

    this.clearError();
    this.recalculate();
  };

  PrePostSelector.prototype.selectedOption = function (panel) {
    return panel.querySelector('[data-pp-option][aria-checked="true"]');
  };

  PrePostSelector.prototype.selectedPlanId = function (panel) {
    var opt = this.selectedOption(panel);
    if (!opt || opt.dataset.kind !== 'plan') return null;
    var id = parseInt(opt.dataset.planId, 10);
    return isNaN(id) ? null : id;
  };

  PrePostSelector.prototype.selectedPlanName = function (panel) {
    var opt = this.selectedOption(panel);
    return (opt && opt.dataset.planName) || null;
  };

  PrePostSelector.prototype.selectedPills = function (panel) {
    return qsa(panel, '[data-pp-pill]').filter(function (p) {
      return p.getAttribute('aria-checked') === 'true';
    });
  };

  // THE TRANSLATION STEP. The card holds one plan id, but that id belongs to
  // one product. Find the equivalent plan on THIS pill's product:
  //   1. the pill already carries that exact id
  //   2. a plan on this product with the same name (same cadence)
  //   3. this product has exactly one plan, so it is the equivalent
  // Returns null when nothing matches — the line then goes in one-time rather
  // than failing the whole /cart/add.js request.
  PrePostSelector.prototype.resolvePlanFor = function (pill, planId, planName) {
    if (planId === null) return null;

    var plans = pillPlans(pill);
    if (!plans.length) return null;

    var i;
    for (i = 0; i < plans.length; i++) {
      if (plans[i].id === planId) return planId;
    }

    if (planName) {
      for (i = 0; i < plans.length; i++) {
        if (plans[i].name && plans[i].name === planName) return plans[i].id;
      }
    }

    return plans.length === 1 ? plans[0].id : null;
  };

  // Shopify's own allocated price for this variant under the resolved plan.
  // Falls back to the card's display percentage when no allocation price is
  // available — that is a DISPLAY estimate. ReCharge charges the real amount.
  PrePostSelector.prototype.planPriceFor = function (pill, planId, panel) {
    if (planId === null) return null;

    var target = this.resolvePlanFor(pill, planId, this.selectedPlanName(panel));
    if (target !== null) {
      var plans = pillPlans(pill);
      for (var i = 0; i < plans.length; i++) {
        if (plans[i].id === target && typeof plans[i].price === 'number') {
          return plans[i].price;
        }
      }
    }

    var opt = panel && this.selectedOption(panel);
    var pct = opt && parseFloat(opt.dataset.displayDiscount);
    if (pct && !isNaN(pct) && pct > 0) {
      return Math.round(intAttr(pill, 'price') * (1 - pct / 100));
    }

    return null;
  };

  /* ---------- plan discovery -------------------------------------------- */
  /* ReCharge (Checkout on Shopify) publishes its subscriptions as native
   * Shopify selling plans. When Liquid can't see them on a referenced
   * product, the same data is on the AJAX product API. Same source, later
   * moment. Nothing here invents a price or a percentage. */

  var PRODUCT_CACHE = {};

  function productRoot() {
    var r = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return (r || '/').replace(/\/$/, '') + '/products/';
  }

  function fetchProduct(handle) {
    if (PRODUCT_CACHE[handle]) return PRODUCT_CACHE[handle];

    PRODUCT_CACHE[handle] = fetch(productRoot() + handle + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .catch(function (err) {
        console.warn(LOG, 'no plan data for "' + handle + '" (' + err.message +
          '). Publish it to the Online Store sales channel — an unpublished ' +
          'product\'s selling plans are invisible to the storefront.');
        return null;
      });

    return PRODUCT_CACHE[handle];
  }

  PrePostSelector.prototype.hydratePlans = function () {
    var self = this;
    var groups = qsa(this.root, '[data-pp-options][data-pp-plan-fetch]');

    if (!groups.length) {
      if (DEBUG) {
        console.warn(LOG, 'no tab is requesting a plan fetch — the updated ' +
          'kit-purchase-options.liquid is not installed.');
      }
      return;
    }

    var wanted = {};
    groups.forEach(function (g) {
      (g.dataset.ppPlanFetch || '').split(',').forEach(function (h) {
        h = h.trim();
        if (h) wanted[h] = true;
      });
    });

    var handles = Object.keys(wanted);
    if (!handles.length) return;

    if (DEBUG) {
      groups.forEach(function (g) {
        var d = g.querySelector('[data-pp-plan-diagnostic]');
        if (d) d.hidden = false;
      });
      console.log(LOG, 'fetching plans for', handles);
    }

    Promise.all(handles.map(fetchProduct)).then(function (products) {
      var cardPlan = null;  // the ONE plan the card will represent
      var allocs = {};      // variantId -> [{ id, price, name }]
      var report = [];

      products.forEach(function (p, i) {
        if (!p) {
          report.push(handles[i] + ': NOT REACHABLE (not published to Online Store)');
          return;
        }

        var g = 0, a = 0;

        // FIRST PLAN OF THIS PRODUCT ONLY. Every child carries the same set
        // of cadences, so taking them all rendered ~25 duplicate cards.
        var mine = null;
        (p.selling_plan_groups || []).forEach(function (grp) {
          (grp.selling_plans || []).forEach(function (plan) {
            g++;
            if (mine) return;
            mine = {
              id: plan.id,
              name: plan.name || grp.name || 'Subscribe & Save',
              description: plan.description || grp.name || 'Delivered on schedule',
              base: 0,
              price: 0
            };
          });
        });

        (p.variants || []).forEach(function (v) {
          var keep = [];
          (v.selling_plan_allocations || []).forEach(function (al) {
            a++;
            var id = al.selling_plan_id;

            // A plan can appear in allocations without appearing in the
            // groups array; adopt it so the card is not lost.
            if (!mine) {
              mine = {
                id: id,
                name: 'Subscribe & Save',
                description: 'Delivered on schedule',
                base: 0,
                price: 0
              };
            }
            if (id !== mine.id || keep.length) return;

            keep.push({ id: id, price: al.price, name: mine.name });
            mine.base += v.price;
            mine.price += al.price;
          });
          if (keep.length) allocs[v.id] = keep;
        });

        // First product with a plan defines the card.
        if (mine && !cardPlan) cardPlan = mine;

        report.push(p.handle + ': plans ' + g + ', allocations ' + a);
      });

      if (DEBUG) console.log(LOG, 'product API says:\n  ' + report.join('\n  '));

      self.applyFetchedPlans(groups, cardPlan, allocs, report);
    });
  };

  PrePostSelector.prototype.applyFetchedPlans = function (groups, cardPlan, allocs, report) {
    var self = this;

    // Back-fill each pill's plan JSON so resolvePlanFor() can translate and
    // recalculate() can price from Shopify's own numbers. Never overwrite
    // what Liquid already emitted — that data is equally authoritative.
    qsa(this.root, '[data-pp-pill]').forEach(function (pill) {
      var vid = parseInt(pill.dataset.variantId, 10);
      var found = allocs[vid];
      if (!found || !found.length) return;
      if (pillPlans(pill).length) return;

      var node = pill.querySelector('[data-pp-plans]');
      if (!node) {
        node = document.createElement('script');
        node.type = 'application/json';
        node.setAttribute('data-pp-plans', '');
        pill.appendChild(node);
      }
      node.textContent = JSON.stringify(found);
    });

    groups.forEach(function (group) {
      var existing = group.querySelector('[data-pp-option][data-kind="plan"]');
      var added = 0;

      // ONE CARD. Skip entirely if Liquid already rendered one.
      if (!existing && cardPlan) {
        var slot = group.querySelector('[data-pp-plan-slot]');
        var wantsDate = group.dataset.ppWantsDate === 'true';

        var pct = 0;
        if (cardPlan.base > 0 && cardPlan.price > 0 && cardPlan.price < cardPlan.base) {
          pct = Math.round((cardPlan.base - cardPlan.price) / cardPlan.base * 100);
        }

        var card = document.createElement('div');
        card.className = 'pp-option';
        card.setAttribute('data-pp-option', '');
        card.setAttribute('data-kind', 'plan');
        card.setAttribute('data-plan-id', String(cardPlan.id));
        card.setAttribute('data-plan-name', cardPlan.name);
        if (pct > 0) card.setAttribute('data-display-discount', String(pct));
        if (wantsDate) card.setAttribute('data-reveal-details', 'true');
        card.setAttribute('role', 'radio');
        card.setAttribute('tabindex', '0');
        card.setAttribute('aria-checked', 'false');

        var badge = document.createElement('span');
        badge.className = 'pp-option-badge';
        if (pct > 0) { badge.textContent = 'Save ' + pct + '%'; }
        else { badge.hidden = true; }

        var body = document.createElement('span');
        body.className = 'pp-option-body';

        var radio = document.createElement('span');
        radio.className = 'pp-option-radio';
        radio.setAttribute('aria-hidden', 'true');

        var main = document.createElement('span');
        main.className = 'pp-option-main';

        var title = document.createElement('span');
        title.className = 'pp-option-title';
        title.textContent = 'Subscribe & Save';

        var sub = document.createElement('span');
        sub.className = 'pp-option-sub';
        sub.textContent = cardPlan.description || cardPlan.name;

        var note = document.createElement('span');
        note.className = 'pp-option-note';
        note.textContent = 'Pause or cancel anytime';

        main.appendChild(title);
        main.appendChild(sub);
        main.appendChild(note);
        body.appendChild(radio);
        body.appendChild(main);
        card.appendChild(badge);
        card.appendChild(body);

        if (slot && slot.parentNode) {
          slot.parentNode.insertBefore(card, slot.nextSibling);
        } else {
          group.appendChild(card);
        }
        added = 1;
      }

      var diag = group.querySelector('[data-pp-plan-diagnostic]');
      if (diag) {
        var have = added || existing ? 1 : 0;
        if (have && !DEBUG) {
          diag.hidden = true;
        } else {
          diag.textContent = have
            ? ('OK — subscribe option available. ' + (report || []).join(' | '))
            : ('No ReCharge plans reachable. ' + (report || []).join(' | ') +
               ' — plans must be attached to these child products and each ' +
               'child must be published to the Online Store sales channel.');
        }
      }
    });

    // The plan card may only exist now, so this is the moment a plan-only tab
    // can finally drop its one-time card.
    this.panels.forEach(function (panel) { self.enforcePlanOnly(panel); });

    // Pill prices may have changed now that allocations exist.
    this.recalculate();
  };

  /* ---------- totals ----------------------------------------------------- */

  PrePostSelector.prototype.recalculate = function () {
    var panel = this.activePanel();
    if (!panel) return;

    var self = this;
    var planId = this.selectedPlanId(panel);
    var total = 0;      // what the storefront quotes, before the kit discount
    var compare = 0;    // undiscounted, one-time, for the strike-through

    this.selectedPills(panel).forEach(function (pill) {
      var qtyPre = intAttr(pill, 'qtyPre');
      var qtyPost = intAttr(pill, 'qtyPost');
      var oneTime = intAttr(pill, 'price');
      var alloc = self.planPriceFor(pill, planId, panel);
      var postUnit = alloc === null ? oneTime : alloc;

      // Pre-op units are ALWAYS charged at the one-time price, even when a
      // plan is selected on the bundle. Only recovery units get the plan rate.
      var line = oneTime * qtyPre + postUnit * qtyPost;

      total += line;
      compare += oneTime * (qtyPre + qtyPost);

      var priceEl = pill.querySelector('[data-role="pill-price"]');
      if (priceEl) priceEl.textContent = formatMoney(line, self.moneyFormat);
    });

    // Kit-level discount, bundle tab only. DISPLAY figure — the money is
    // enforced by an automatic discount in Shopify.
    var pct = floatAttr(panel, 'discountPct');
    var payable = pct > 0 ? Math.round(total * (1 - pct / 100)) : total;
    if (pct > 0) compare = Math.max(compare, total);

    var text = formatMoney(payable, this.moneyFormat);

    if (this.footerTotal) this.footerTotal.textContent = text;
    if (this.headlinePrice) this.headlinePrice.textContent = text;

    var phaseTotal = panel.querySelector('[data-role="phase-total"]');
    if (phaseTotal) phaseTotal.textContent = text;

    var saved = compare - payable;

    if (this.headlineCompare) {
      if (saved > 0) {
        this.headlineCompare.textContent = formatMoney(compare, this.moneyFormat);
        this.headlineCompare.hidden = false;
      } else {
        this.headlineCompare.hidden = true;
      }
    }

    if (this.headlineSave) {
      if (saved > 0) {
        this.headlineSave.textContent = 'Save ' + formatMoney(saved, this.moneyFormat);
        this.headlineSave.hidden = false;
      } else {
        this.headlineSave.hidden = true;
      }
    }

    this.themePriceTargets.forEach(function (node) { node.textContent = text; });
  };

  /* ---------- cart ------------------------------------------------------- */

  PrePostSelector.prototype.buildPayload = function () {
    var self = this;
    var panel = this.activePanel();
    if (!panel) return { error: 'Nothing selected yet.' };

    var kitTitle = panel.dataset.kitTitle || '';
    var planId = this.selectedPlanId(panel);
    var planName = this.selectedPlanName(panel);

    // The cart snippet groups lines by the Kit property. Without it the lines
    // still appear, just ungrouped and without the coloured kit header.
    if (!kitTitle) {
      console.warn(LOG, 'panel "' + panel.dataset.phase + '" has no ' +
        'data-kit-title — its lines will not group in the cart drawer.');
    }

    // Every required section must have something chosen. Naming the section
    // in the message beats a generic "make a selection".
    var missing = null;
    qsa(panel, '[data-picker-block][data-required="true"]').forEach(function (blockEl) {
      if (missing) return;
      var any = qsa(blockEl, '[data-pp-pill]').some(function (p) {
        return p.getAttribute('aria-checked') === 'true';
      });
      if (!any) missing = blockEl.dataset.sectionLabel || 'a required option';
    });
    if (missing) return { error: 'Choose ' + missing.toLowerCase() + ' to continue.' };

    var pills = this.selectedPills(panel);
    if (!pills.length) return { error: 'Choose at least one item before adding to cart.' };

    // The kit parent is an empty container. If it ever appears as one of its
    // own metafield children, adding it would put a zero-priced mystery line
    // in the drawer, so it is dropped here.
    var parentId = pageVariantId();

    var items = [];
    var subscribed = 0;
    var skipped = [];

    pills.forEach(function (pill) {
      var variantId = parseInt(pill.dataset.variantId, 10);
      if (isNaN(variantId)) return;

      if (parentId && String(variantId) === parentId) {
        console.warn(LOG, 'skipping variant ' + variantId + ' — it is this ' +
          'page\'s own kit parent, which is never sold.');
        return;
      }

      var qtyPre = intAttr(pill, 'qtyPre');
      var qtyPost = intAttr(pill, 'qtyPost');

      // Two lines for the SAME variant stay separate because the Phase
      // property differs. Without that Shopify merges them and the selling
      // plan would swallow the pre-op units too.
      if (qtyPre > 0) {
        items.push({
          id: variantId,
          quantity: qtyPre,
          properties: { Kit: kitTitle, Phase: 'Pre-op' }
        });
      }

      if (qtyPost > 0) {
        var line = {
          id: variantId,
          quantity: qtyPost,
          properties: { Kit: kitTitle, Phase: 'Recovery' }
        };

        // Each line gets ITS OWN product's equivalent plan id.
        var resolved = self.resolvePlanFor(pill, planId, planName);
        if (resolved !== null) {
          line.selling_plan = resolved;
          subscribed++;
        } else if (planId !== null) {
          skipped.push(pill.dataset.productTitle || String(variantId));
        }

        items.push(line);
      }
    });

    if (!items.length) {
      return { error: 'Nothing to add. Check the kit quantities in the theme editor.' };
    }

    if (DEBUG) {
      console.log(LOG, 'sending ' + items.length + ' lines:');
      items.forEach(function (i) {
        console.log('   variant ' + i.id + ' × ' + i.quantity +
          ' | ' + i.properties.Phase +
          (i.selling_plan ? ' | plan ' + i.selling_plan : ' | one-time'));
      });
      if (planId !== null) {
        console.log(LOG, 'subscribed lines: ' + subscribed +
          (skipped.length ? ' | one-time (no matching plan): ' + skipped.join(', ') : ''));
      }
    }

    var attributes = {
      _kit_type: panel.dataset.phase,
      _kit_title: kitTitle
    };

    var dateEl = panel.querySelector('[data-pp-surgery-date]');
    if (planId !== null && dateEl && dateEl.value) {
      attributes._surgery_date = dateEl.value;
    }

    return { items: items, attributes: attributes };
  };

  PrePostSelector.prototype.addToCart = function () {
    if (this.busy) return;

    var payload = this.buildPayload();
    if (payload.error) return this.showError(payload.error);

    var self = this;
    this.setBusy(true);
    this.clearError();

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: payload.items })
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (!res.ok) {
            throw new Error(body.description || body.message || 'That could not be added.');
          }
          return body;
        });
      })
      .then(function () {
        // Second call: /cart/add.js silently ignores an attributes key.
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: payload.attributes })
        });
      })
      .then(function () { return self.syncCartCount(); })
      .then(function () { return self.afterAddToCart(); })
      .catch(function (err) {
        console.error(LOG, 'add to cart failed', err);
        self.showError(err.message);
      })
      .then(function () { self.setBusy(false); });
  };

  // The header bubble reads the data-cart-count ATTRIBUTE — the number is
  // CSS-drawn from it. Writing textContent would wipe the icon's <svg>.
  PrePostSelector.prototype.syncCartCount = function () {
    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        qsa(document, '[data-cart-count]').forEach(function (el) {
          el.setAttribute('data-cart-count', cart.item_count);
        });
        document.dispatchEvent(new CustomEvent('cart:count-updated', {
          bubbles: true,
          detail: { count: cart.item_count }
        }));
      })
      .catch(function () {});
  };

  /* ---------- keeping the drawer in sync with the cart ------------------- */

  // The cart section node the drawer renders into.
  PrePostSelector.prototype.cartSectionEl = function () {
    return document.querySelector('cart-element[data-view="drawer"]') ||
           document.querySelector('cart-element') ||
           document.querySelector('cart-drawer');
  };

  // How many line items the drawer is currently showing. -1 when there is no
  // node to inspect, which we treat as "can't tell, don't fight it".
  PrePostSelector.prototype.drawerLineCount = function () {
    var cartEl = this.cartSectionEl();
    if (!cartEl) return -1;
    return qsa(cartEl, ITEM_SEL).length;
  };

  // Repaint the drawer from the real cart, then make the result VISIBLE.
  // `expected` is how many lines /cart.js says there should be; pass it so
  // the empty state can be set correctly and an empty render can be judged.
  PrePostSelector.prototype.refreshCartDrawer = function (expected) {
    var cartEl = this.cartSectionEl();
    if (!cartEl) return Promise.resolve(-1);

    var self = this;
    var view = cartEl.getAttribute('data-view');
    var sectionId = cartEl.getAttribute('data-section-id');

    // Re-query: renderIntoDrawer may have replaced the node entirely.
    function finish(count) {
      var live = self.cartSectionEl();
      activateTransitions(live);
      applyEmptyState(live, expected);
      return count;
    }

    if (typeof cartEl.refresh === 'function') {
      try {
        return Promise.resolve(cartEl.refresh())
          .then(function () { return wait(140); })
          .then(function () {
            var n = self.drawerLineCount();
            // Trust the native refresh only if it produced the right count.
            if ((typeof expected !== 'number' && n > 0) || n === expected) {
              return finish(n);
            }
            return self.renderIntoDrawer(cartEl, sectionId, view, expected).then(finish);
          })
          .catch(function () {
            return self.renderIntoDrawer(cartEl, sectionId, view, expected).then(finish);
          });
      } catch (e) { /* fall through */ }
    }

    return this.renderIntoDrawer(cartEl, sectionId, view, expected).then(finish);
  };

  // Section-render path. An empty render never overwrites a drawer that is
  // already showing lines.
  PrePostSelector.prototype.renderIntoDrawer = function (cartEl, sectionId, view, expected) {
    if (!sectionId) {
      console.warn(LOG, 'cart-element has no data-section-id — it cannot be ' +
        'repainted. The cart snippet must be rendered from a section.');
      return Promise.resolve(this.drawerLineCount());
    }

    var self = this;
    var wasOpen = drawerIsOpen();

    return renderSection('/?sections=' + sectionId, sectionId, view)
      .then(function (a) {
        if (a && a.count > 0) return a;
        return renderSection('/cart?sections=' + sectionId, sectionId, view)
          .then(function (b) { return (b && b.count > 0) ? b : (a || b); });
      })
      .then(function (best) {
        if (!best) return self.drawerLineCount();

        var have = self.drawerLineCount();
        if (best.count === 0 && have > 0 && expected !== 0) return have;

        var adopted = adoptCartElement(cartEl, best.node);

        if (!adopted) {
          // No parent to replace into — fall back to the old swap.
          cartEl.innerHTML = best.node.innerHTML;
        }

        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('theme:cart:updated', { bubbles: true }));

        // Replacing the node can drop the open state if the theme put it on
        // cart-element rather than on a parent drawer wrapper.
        if (wasOpen && !drawerIsOpen()) {
          return tryToOpenDrawer(self.root).then(function () { return best.count; });
        }

        return best.count;
      });
  };

  // Compare what the drawer shows against what /cart.js holds and repaint
  // until they agree. Up to seven passes over roughly two seconds.
  //
  // The visibility fixes run on EVERY pass, including the one where the
  // counts already match — that is the case where the right lines are painted
  // but sit at opacity 0 under the empty message.
  PrePostSelector.prototype.ensureDrawerFresh = function (attempt) {
    attempt = attempt || 0;
    var self = this;

    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var want = cart.items.length;
        var have = self.drawerLineCount();
        var cartEl = self.cartSectionEl();

        activateTransitions(cartEl);
        applyEmptyState(cartEl, want);

        if (have === -1) return false;

        // Counts agree but the component may still be in its stale empty
        // state. On the first pass, force one node replacement to be sure.
        if (have === want) {
          if (attempt === 0 && want > 0 && cartEl && cartEl.querySelector('.cart--empty') &&
              getComputedStyle(cartEl.querySelector('.cart--empty')).display !== 'none') {
            return self.refreshCartDrawer(want).then(function () { return true; });
          }
          return true;
        }

        if (attempt >= 6) {
          console.warn(LOG, 'drawer shows ' + have + ' lines, cart has ' + want +
            '. Run window.__ppSection() and send the output.');
          return false;
        }

        return self.refreshCartDrawer(want)
          .then(function () { return wait(120 + attempt * 150); })
          .then(function () { return self.ensureDrawerFresh(attempt + 1); });
      })
      .catch(function () { return false; });
  };

  /* ---------- the theme-replay drawer path ------------------------------ */

  // The parent variant the theme's own button would submit. Only meaningful
  // on a non-kit template, where that submit actually carries a variant.
  PrePostSelector.prototype.mainVariantId = function (btn) {
    var id = null;

    var holder = btn && btn.closest && btn.closest('product-buy-buttons-element');
    if (holder && holder.dataset.variantId) id = holder.dataset.variantId;

    if (!id) id = pageVariantId();

    if (!id) return null;

    // Hard guard: never treat one of our own kit children as the parent.
    var panel = this.activePanel();
    var clash = panel && qsa(panel, '[data-pp-pill]').some(function (p) {
      return String(p.dataset.variantId) === String(id);
    });
    if (clash) {
      console.warn(LOG, 'resolved parent variant ' + id + ' is one of the kit ' +
        'children — refusing to remove it.');
      return null;
    }

    return id;
  };

  PrePostSelector.prototype.zeroLine = function (variantId) {
    var updates = {};
    updates[variantId] = 0;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).catch(function (err) {
      console.error(LOG, 'parent removal failed', err);
    });
  };

  // Called by the fetch hook the moment the theme's own /cart/add lands.
  PrePostSelector.prototype.removeMainAndRefresh = function (variantId) {
    if (!variantId) return Promise.resolve();
    var self = this;
    return this.zeroLine(variantId)
      .then(function () { return self.ensureDrawerFresh(0); })
      .then(function () {
        unhideVariantInDrawer();
        return self.syncCartCount();
      });
  };

  // Runs after the replay, whether or not a parent line was created.
  PrePostSelector.prototype.settleDrawer = function (mainId) {
    var self = this;

    return wait(250)
      .then(function () {
        // If the fetch hook already handled it, the flag is null by now.
        if (!mainId || window.__ppPendingMainRemoval !== mainId) return;
        window.__ppPendingMainRemoval = null;
        console.warn(LOG, 'theme add never fired through fetch — cleaning up.');
        return self.zeroLine(mainId);
      })
      .then(function () { return self.ensureDrawerFresh(0); })
      .then(function () {
        unhideVariantInDrawer();
        return self.syncCartCount();
      })
      // Second convergence pass. Some themes repaint the drawer from cached
      // state when their open animation finishes, which lands after ours.
      .then(function () { return wait(550); })
      .then(function () { return self.ensureDrawerFresh(0); })
      .catch(function () {});
  };

  // Hand the click back to the theme so its native flow opens AND paints the
  // drawer. Returns the settle promise, or null when there is no button.
  PrePostSelector.prototype.openDrawerViaTheme = function () {
    var btn = this.lastThemeBtn || this.themeBuyButtons()[0];
    if (!btn) return null;

    // On a kit template the snippet renders no name="id" field, so the
    // theme's own add carries no variant and creates no line. Nothing to
    // hide, nothing to delete — we just let its flow run.
    var mainId = isKitTemplate() ? null : this.mainVariantId(btn);

    if (mainId) {
      hideVariantInDrawer(mainId);
      window.__ppPendingMainRemoval = mainId;
    }

    btn.dataset.ppReplay = 'true';
    btn.click();

    return this.settleDrawer(mainId);
  };

  /* ---------- the events drawer path ------------------------------------ */

  // Fallback for when there is no theme button to replay. We open and paint
  // the drawer entirely ourselves.
  PrePostSelector.prototype.openDrawerViaEvents = function () {
    var self = this;

    return this.refreshCartDrawer()
      .then(function () { return tryToOpenDrawer(self.root); })
      .then(function (how) {
        if (!how) {
          console.warn(LOG, 'none of the standard hooks opened the drawer. ' +
            'Run window.__ppOpenDrawer() and send the output.');
          return;
        }
        if (DEBUG) console.log(LOG, 'drawer opened via: ' + how);

        return wait(150)
          .then(function () { return self.ensureDrawerFresh(0); })
          .then(function () { return wait(550); })
          .then(function () { return self.ensureDrawerFresh(0); });
      });
  };

  PrePostSelector.prototype.afterAddToCart = function () {
    if (this.afterAdd === 'cart') { window.location.href = '/cart'; return; }
    if (this.afterAdd === 'none') return;

    // Preferred: the theme's own flow, which is the only thing that reliably
    // both opens and fills the drawer on this theme.
    if (this.drawerMode !== 'events' && this.themeButtonMode !== 'hide') {
      var settling = this.openDrawerViaTheme();
      if (settling) return settling;
      console.warn(LOG, 'no theme button to replay — using the events path.');
    }

    return this.openDrawerViaEvents();
  };

  /* ---------- ui state --------------------------------------------------- */

  PrePostSelector.prototype.setBusy = function (on) {
    this.busy = on;
    if (!this.addBtn) return;
    this.addBtn.classList.toggle('is-busy', on);
    this.addBtn.setAttribute('aria-busy', on ? 'true' : 'false');
    var label = this.addBtn.querySelector('[data-role="add-label"]');
    if (!label) return;
    if (on) {
      label.dataset.idle = label.textContent;
      label.textContent = 'Adding…';
    } else if (label.dataset.idle) {
      label.textContent = label.dataset.idle;
    }
  };

  PrePostSelector.prototype.showError = function (msg) {
    if (!this.errorEl) { console.error(LOG, msg); return; }
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  };

  PrePostSelector.prototype.clearError = function () {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  };

  PrePostSelector.prototype.hideDynamicCheckout = function () {
    qsa(document, '.shopify-payment-button, [data-shopify="payment-button"], .product-buy-buttons--smart')
      .forEach(function (el) { el.style.display = 'none'; });
  };

  /* ---------- boot -------------------------------------------------------- */

  function init() {
    qsa(document, '[data-pp-selector]').forEach(function (root) {
      if (root.__ppBound) return;
      try {
        // Flag set only AFTER the constructor succeeds. Setting it first meant
        // a throw left the selector dead but marked as booted, so the
        // section:load retry skipped it and diagnostics reported a clean boot.
        root.__ppInstance = new PrePostSelector(root);
        root.__ppBound = true;
      } catch (err) {
        console.error(LOG, 'failed to initialise', err);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  /* ---------- console diagnostics ---------------------------------------- */

  // window.__ppQty() — per pill: its REAL handle, and the quantity Liquid
  // resolved. A pill showing 1 where you set 3 means that handle does not
  // match what you typed in the customizer, and Liquid fell back to 1.
  window.__ppQty = function () {
    var inst = window.__pp();

    console.log(LOG, 'kit template: ' + isKitTemplate() +
      ' | drawer mode: ' + (inst ? inst.drawerMode : 'NO INSTANCE — constructor threw'));
    console.log(LOG, 'page (kit parent) variant: ' + (pageVariantId() || 'not found'));

    qsa(document, '[data-pp-panel]').forEach(function (panel) {
      var pills = qsa(panel, '[data-pp-pill]');
      console.log(LOG, 'panel "' + panel.dataset.phase + '" — ' +
        (panel.dataset.kitTitle || 'NO KIT TITLE') + ' (' + pills.length + ' pills)');

      pills.forEach(function (pill) {
        console.log('   ' + (pill.dataset.handle || '(no data-handle — update kit-pill-group.liquid)') +
          '  →  pre ' + intAttr(pill, 'qtyPre') + ', post ' + intAttr(pill, 'qtyPost') +
          (pill.getAttribute('aria-checked') === 'true' ? '  [selected]' : '') +
          '   | ' + pill.dataset.productTitle);
      });
    });

    console.log(LOG, 'copy the handles above into the quantity fields EXACTLY ' +
      'as shown, e.g. "<handle>:3".');
  };

  // window.__ppOpenDrawer() — which hook opens the drawer. Run it CLOSED.
  window.__ppOpenDrawer = function () {
    var nodes = drawerNodes();

    console.log(LOG, 'drawer candidates found: ' + nodes.length);
    nodes.forEach(function (n) {
      console.log('   <' + n.tagName.toLowerCase() + '>',
        'attrs: ' + n.getAttributeNames().join(' '),
        '| methods: ' + Object.getOwnPropertyNames(Object.getPrototypeOf(n)).join(','),
        '| onScreen: ' + isOnScreen(n));
    });

    OPENER_SELECTORS.forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el) console.log('   opener candidate ' + sel + ' → <' + el.tagName.toLowerCase() + ' class="' + el.className + '">');
    });

    var rootEl = document.querySelector('[data-pp-selector]');
    return tryToOpenDrawer(rootEl).then(function (how) {
      console.log(LOG, how ? ('OPENED VIA: ' + how) : 'nothing opened it.');
      return how;
    });
  };

  // window.__ppSection() — is it the CART, the DRAWER DOM, the SECTION
  // RENDER, or just VISIBILITY? Run right after an add.
  window.__ppSection = function () {
    var inst = window.__pp();
    var cartEl = inst ? inst.cartSectionEl() : document.querySelector('cart-element');
    var sectionId = cartEl && cartEl.getAttribute('data-section-id');
    var view = cartEl && cartEl.getAttribute('data-view');
    var parentId = pageVariantId();

    console.log(LOG, 'drawer node:', cartEl ? '<' + cartEl.tagName.toLowerCase() + '>' : 'NOT FOUND',
      '| section id:', sectionId || 'NONE', '| view:', view || 'none',
      '| native refresh():', cartEl && typeof cartEl.refresh === 'function');
    console.log(LOG, 'kit template:', isKitTemplate(),
      '| drawer mode:', inst ? inst.drawerMode : 'NO INSTANCE — constructor threw',
      '| theme buy button:', !!document.querySelector('.product-buy-buttons--primary'),
      '| theme component:', !!document.querySelector('product-buy-buttons-element'));

    if (!sectionId) {
      console.log(LOG, 'no data-section-id — the drawer can never be repainted.');
    }

    return fetch('/cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        console.log(LOG, 'CART holds ' + cart.items.length + ' lines:');
        cart.items.forEach(function (i) {
          var isParent = parentId && String(i.variant_id) === parentId;
          console.log('   ' + i.quantity + ' x ' + i.title +
            (i.selling_plan_allocation ? ' [subscription]' : '') +
            ' | variant ' + i.variant_id +
            (isParent ? '   <-- KIT PARENT, SHOULD NOT BE HERE' : ''));
        });

        var painted = cartEl ? qsa(cartEl, ITEM_SEL) : [];
        console.log(LOG, 'DRAWER DOM shows ' + painted.length + ' line elements');

        if (painted.length) {
          var cs = getComputedStyle(painted[0]);
          console.log(LOG, 'first line: opacity ' + cs.opacity + ', display ' +
            cs.display + ', transition-active=' +
            painted[0].getAttribute('data-transition-active'));
        }

        var empty = cartEl && cartEl.querySelector('.cart--empty');
        if (empty) {
          console.log(LOG, 'empty message display: ' +
            getComputedStyle(empty).display + ' | is-empty class: ' +
            cartEl.classList.contains('is-empty'));
        }

        if (!sectionId) return null;

        return Promise.all([
          renderSection('/?sections=' + sectionId, sectionId, view),
          renderSection('/cart?sections=' + sectionId, sectionId, view)
        ]).then(function (r) {
          console.log(LOG, 'SECTION RENDER /?sections=  → ' +
            (r[0] ? r[0].count + ' lines' : 'nothing'));
          console.log(LOG, 'SECTION RENDER /cart?sections= → ' +
            (r[1] ? r[1].count + ' lines' : 'nothing'));
          return r;
        });
      });
  };

  // window.__pp() — the live instance, e.g. window.__pp().recalculate()
  window.__pp = function () {
    var r = document.querySelector('[data-pp-selector]');
    return r && r.__ppInstance;
  };

  // window.__ppPlans() — per child product: reachable, and what plans it has.
  window.__ppPlans = function () {
    var groups = qsa(document, '[data-pp-options][data-pp-plan-fetch]');
    if (!groups.length) {
      console.log(LOG, 'No tab is requesting a plan fetch — the updated ' +
        'kit-purchase-options.liquid is not installed.');
      return;
    }
    var wanted = {};
    groups.forEach(function (g) {
      (g.dataset.ppPlanFetch || '').split(',').forEach(function (h) {
        h = h.trim();
        if (h) wanted[h] = true;
      });
    });
    var handles = Object.keys(wanted);
    console.log(LOG, 'checking', handles.length, 'child products:', handles);

    return Promise.all(handles.map(function (h) {
      return fetch(productRoot() + h + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (ps) {
      ps.forEach(function (p, i) {
        if (!p) {
          console.log('  ' + handles[i] + ' → NOT FETCHABLE (not published to Online Store)');
          return;
        }
        var names = [];
        (p.selling_plan_groups || []).forEach(function (grp) {
          (grp.selling_plans || []).forEach(function (pl) { names.push(pl.name); });
        });
        var a = (p.variants || []).reduce(function (n, v) {
          return n + ((v.selling_plan_allocations || []).length);
        }, 0);
        console.log('  ' + p.handle + ' → allocations: ' + a + ' | plans: ' +
          (names.length ? names.join(' / ') : 'none'));
      });
      console.log(LOG, 'the FIRST plan name on each product is what the card ' +
        'matches against. If those names differ across products, some lines ' +
        'will fall back to one-time.');
    });
  };
})();